/// <amd-module name="Wit/Common/WorkItemHeaderActionService/WorkItemHeaderActionService" />
import * as React from "react";
import { ITfsNavigationSettingsService } from "Tfs/Platform/NavigationSettings";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IReadonlyObservableArray, ObservableArray } from "VSS/Core/Observable";
import { IMenuService } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssLocationService } from "VSS/Platform/Location";
import { traceException } from "VSS/Platform/Trace";
import { Button } from "VSSUI/Button";
import { IMenuItem, IMenuItemDetails, MenuCell } from "VSSUI/Menu";
import { css, KeyCode } from "VSSUI/Util";
import * as CommonResources from "Wit/Common/Resources";
import { IColorAndIcon, WorkItemTypeColorAndIcons } from "Wit/Common/WorkItemTypeColorAndIcons";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";

interface IWorkItemTypeColorAndIcon extends IColorAndIcon {
    workItemTypeName: string;
}

interface INewWorkItemData {
    workItemTypes?: string[];
    colorAndIcons?: { [name: string]: IWorkItemTypeColorAndIcon };
    defaultMenuItem?: string;
    hasNewWorkItemPermission?: boolean;
}

interface IPinnedMenuItem extends IMenuItem {
    pinned: boolean;
}

/**
 * Service for creating menu items for work item actions.
 */
class WorkItemActionsService extends VssService implements IMenuService {
    private _topLevelMenuItems = new ObservableArray<IMenuItem>();
    private _unpinnedMenuItems = new ObservableArray<IMenuItem>();
    private _settingsService: ITfsNavigationSettingsService;
    private _typeMenuItems: IPinnedMenuItem[] = [];
    private _newWorkItem: IMenuItem;
    private _pinnedItems: string[] = [];

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        let projectName: string | undefined;
        let teamName: string | undefined;

        // Try and get the project and team name from the page data
        if (tfsPageData) {
            if (tfsPageData.project) {
                projectName = tfsPageData.project.name;
            }

            const teamData = tfsPageService.getHorizontalNavigationTeamData();
            if (teamData) {
                teamName = teamData.name;
            }
        }

        // Load the new work item data.
        let workItemData: INewWorkItemData | undefined;
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getDataAsync<INewWorkItemData>("ms.vss-work-web.new-workitem-data-provider").then(result => {
            workItemData = result || {};

            if (workItemData.hasNewWorkItemPermission && workItemData.workItemTypes) {
                // Look up pinned items
                this._settingsService = this.pageContext.getService<ITfsNavigationSettingsService>("ITfsNavigationSettingsService");
                let pinnedItems: string[] | undefined = this._settingsService.getUserSetting<string[]>("PinnedWorkAction");

                // If no pinned items, then add the default work item to the pinned menu
                if (!pinnedItems) {
                    pinnedItems = workItemData.defaultMenuItem ? [`new-workitem-${workItemData.defaultMenuItem}`] : [];
                }

                // Save the pinnedItems or the default.
                this._pinnedItems = pinnedItems;

                // Build the set of pinned menu items on the top level menu.
                if (projectName && workItemData && workItemData.workItemTypes) {
                    const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");

                    for (let i = 0; i < workItemData.workItemTypes.length; i++) {
                        this._typeMenuItems.push(
                            this._createMenuItem(projectName, teamName, locationService, workItemData, workItemData.workItemTypes[i])
                        );
                    }

                    this._typeMenuItems.sort((m1: IMenuItem, m2: IMenuItem) => {
                        if (m1.text && m2.text) {
                            return m1.text.localeCompare(m2.text, navigator.language);
                        } else {
                            return 0;
                        }
                    });

                    // Add the pinned menu items to the proper menu.
                    for (const typeMenuItem of this._typeMenuItems) {
                        if (typeMenuItem.pinned) {
                            this._topLevelMenuItems.push(typeMenuItem);
                        } else {
                            this._unpinnedMenuItems.push(typeMenuItem);
                        }
                    }
                }

                // Create the "New Work Item" sub menu item.
                this._newWorkItem = {
                    id: "new-work-item",
                    rank: 200,
                    text: CommonResources.NewWorkItem,
                    groupKey: "wit-actions",
                    subMenuProps: {
                        id: "new-work-item-unpinned",
                        items: this._unpinnedMenuItems
                    }
                };

                if (this._unpinnedMenuItems.length) {
                    this._topLevelMenuItems.splice(0, 0, this._newWorkItem);
                }
            }
        });
    }

    public getMenuItems(menuId: string): IReadonlyObservableArray<IMenuItem> | null {
        if (menuId === "ms.vss-tfs-web.vertical-header-create-menu") {
            return this._topLevelMenuItems;
        }

        return null;
    }

    /**
     * Crates individual menu item.  Determines icon/colr information as well as pinning settings.
     *
     * @param projectName
     * @param teamName
     * @param locationService
     * @param workItemData
     * @param workItemTypeName
     */
    private _createMenuItem(
        projectName: string,
        teamName: string | undefined,
        locationService: IVssLocationService,
        workItemData: INewWorkItemData,
        workItemTypeName: string
    ): IPinnedMenuItem {
        const menuItemId = `new-workitem-${workItemTypeName.replace(/[\s()]/g, "-").toLowerCase()}`;

        // Create href to use when item is selected.
        const routeValues: { [key: string]: string } = {
            project: projectName,
            parameters: workItemTypeName.toLowerCase()
        };

        if (teamName) {
            routeValues["team"] = teamName;
        }

        const item: IPinnedMenuItem = {
            groupKey: "wit-actions",
            href: locationService.routeUrl("ms.vss-work-web.work-items-create-route", routeValues),
            id: menuItemId,
            pinned: this._pinnedItems.indexOf(menuItemId) !== -1,
            rank: 201,
            renderMenuCell: this.renderMenuCell,
            text: workItemTypeName
        };

        // See if we can find the color of this work item type
        if (workItemData.colorAndIcons) {
            const colorAndIcon = this._getWorkItemTypeColorAndIcon(workItemData, workItemTypeName);

            if (colorAndIcon) {
                item.iconProps = { className: css("bowtie-icon", colorAndIcon.icon), style: { color: colorAndIcon.color } };
            }
        }

        item.onActivate = (menuItem: IMenuItem, event: React.MouseEvent<HTMLElement>): boolean | undefined => {
            if (!event.ctrlKey && !event.shiftKey) {
                const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
                contributionService.getService<IWorkItemService>("ms.vss-work-web.work-item-service").then(
                    service => {
                        if (!service) {
                            throw new Error("Cannot create work item creation service");
                        }

                        service.createWorkItem(workItemTypeName);
                    },
                    error => {
                        traceException(this.pageContext, "WIT", "WorkItemCreationFromNavigation", error);
                    }
                );

                event.preventDefault();
                return true;
            }
        };

        return item;
    }

    /**
     * Look up the work item color and icon
     *
     * @param workItemData
     * @param typeName
     */
    private _getWorkItemTypeColorAndIcon(workItemData: INewWorkItemData, typeName: string): IColorAndIcon | undefined {
        // get work item color and icon if it is defined.
        typeName = typeName.trim().toLowerCase();

        if (typeName && workItemData.colorAndIcons) {
            const colorAndIcon = workItemData.colorAndIcons[typeName];
            if (colorAndIcon && colorAndIcon.icon) {
                return WorkItemTypeColorAndIcons.createColorAndIcon(colorAndIcon.color, colorAndIcon.icon);
            } else {
                return WorkItemTypeColorAndIcons.getDefault();
            }
        }

        return undefined;
    }

    /**
     * Add the menuitem into the observable array sorted by text.
     */
    private _insertMenuItem(menuItem: IPinnedMenuItem, menuItems: ObservableArray<IMenuItem>) {
        let index = 0;
        for (; index < menuItems.length; index++) {
            if (menuItems.value[index] === this._newWorkItem) {
                continue;
            }

            if (menuItem.text!.localeCompare(menuItems.value[index].text!, navigator.language) < 0) {
                menuItems.splice(index, 0, menuItem);
                break;
            }
        }

        if (index === menuItems.length) {
            menuItems.push(menuItem);
        }
    }

    private _processPinClick(menuItem: IPinnedMenuItem, details: IMenuItemDetails) {
        const menuId = menuItem.id;

        // Switch the menuitem between pinned and unpinned.
        if (menuItem.pinned) {
            // Move the menuitem from the new work item to the top-level menu.
            this._topLevelMenuItems.splice(this._topLevelMenuItems.value.indexOf(menuItem), 1);
            this._insertMenuItem(menuItem, this._unpinnedMenuItems);
            this._pinnedItems.splice(this._pinnedItems.indexOf(menuId), 1);

            // Add the new work item subMenu if this is the first unpinned menu item.
            if (this._unpinnedMenuItems.length === 1) {
                this._topLevelMenuItems.splice(0, 0, this._newWorkItem);
            }

            // Focus the menu since we are removing an item. This prevents us from
            // moving focus out of the menu.
            details.menu.focus();
        } else {
            // Move the menuitem from the new work item sub menu to the top level.
            this._unpinnedMenuItems.splice(this._unpinnedMenuItems.value.indexOf(menuItem), 1);
            this._insertMenuItem(menuItem, this._topLevelMenuItems);
            this._pinnedItems.push(menuId);

            // If there are no more unpinned menu items remove the new work item submenu.
            if (this._unpinnedMenuItems.length === 0) {
                details.menu.getParent()!.focus();
                this._topLevelMenuItems.splice(this._topLevelMenuItems.value.indexOf(this._newWorkItem), 1);
            } else {
                details.menu.focus();
            }
        }

        // Update the pinned items setting, this is durable across pages.
        this._settingsService.setUserSetting("PinnedWorkAction", this._pinnedItems);

        // Swap the pinned state of the menuItem.
        menuItem.pinned = !menuItem.pinned;
    }

    /**
     * Renders the Pin in the menuItem.
     *
     * @param menuCell
     * @param menuItem
     * @param details
     */
    private renderMenuCell = (menuCell: MenuCell, menuItem: IPinnedMenuItem, details: IMenuItemDetails): JSX.Element | undefined => {
        switch (menuCell) {
            case MenuCell.Action:
                return (
                    <span
                        className={css(menuItem.pinned && "bolt-list-cell-content-reveal")}
                        onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
                            if (!event.defaultPrevented) {
                                if (event.which === KeyCode.leftArrow) {
                                    event.preventDefault();
                                }
                            }
                        }}
                    >
                        <Button
                            ariaLabel={menuItem.pinned ? CommonResources.UnpinWorkItemType : CommonResources.PinWorkItemType}
                            iconProps={{ iconName: menuItem.pinned ? "Unpin" : "Pin" }}
                            onClick={(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                                if (!event.defaultPrevented) {
                                    this._processPinClick(menuItem, details);
                                    event.preventDefault();
                                }
                            }}
                            subtle={true}
                        />
                    </span>
                );
        }
    };
}

Services.add("work-item-action-service", { serviceFactory: WorkItemActionsService });
